import { useEffect } from 'react';
import {
    checkOrderPaymentStatus,
    reconcileOrderPayment
} from '../../../actions/payment/retryPayment';
import { consumePaymentReconciliation } from '../../../helpers/shop/couponSession';

export const PAYMENT_POLL_INTERVAL = 5000;
export const PAYMENT_POLL_DURATION = 20000;
export const PAYMENT_RESUME_DEBOUNCE = 300;

const usePaymentStatusPolling = ({
    orderId,
    enabled,
    onPaid,
    checkStatus: statusRequest = checkOrderPaymentStatus,
    reconcile: reconcileRequest = reconcileOrderPayment,
    reconciliationContext = 'fast-checkout'
}) => {
    useEffect(() => {
        if (!orderId || !enabled) return undefined;
        let interval;
        let deadline = 0;
        let disposed = false;
        let checking = false;
        let reconciling = false;
        let paid = false;
        let resumeTimeout;

        const stopPolling = () => {
            if (interval) clearInterval(interval);
            interval = undefined;
        };
        const checkStatus = async () => {
            if (disposed || checking || paid) return;
            checking = true;
            const result = await statusRequest(orderId);
            checking = false;
            if (!disposed && !paid && result?.status && result.resp?.state === 'approved') {
                paid = true;
                stopPolling();
                onPaid(result.resp.reference);
            }
        };
        const startPolling = () => {
            stopPolling();
            deadline = Date.now() + PAYMENT_POLL_DURATION;
            checkStatus();
            interval = setInterval(() => {
                if (Date.now() >= deadline) stopPolling();
                else checkStatus();
            }, PAYMENT_POLL_INTERVAL);
        };
        const reconcileIfReturning = async () => {
            if (disposed || paid || reconciling || !consumePaymentReconciliation({
                orderId,
                context: reconciliationContext
            })) return;
            reconciling = true;
            const result = await reconcileRequest(orderId);
            reconciling = false;
            if (!disposed && !paid &&
                (result.resp?.state === 'approved' || result.msg === 'order-already-paid')) {
                paid = true;
                stopPolling();
                onPaid(result.resp?.reference);
            }
        };
        const resumeChecks = () => {
            if (disposed || paid) return;
            if (resumeTimeout) clearTimeout(resumeTimeout);
            resumeTimeout = setTimeout(() => {
                reconcileIfReturning();
                startPolling();
            }, PAYMENT_RESUME_DEBOUNCE);
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') resumeChecks();
        };

        reconcileIfReturning();
        startPolling();
        window.addEventListener('focus', resumeChecks);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            disposed = true;
            checking = false;
            reconciling = false;
            if (resumeTimeout) clearTimeout(resumeTimeout);
            stopPolling();
            window.removeEventListener('focus', resumeChecks);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [orderId, enabled, onPaid, statusRequest, reconcileRequest, reconciliationContext]);
};

export default usePaymentStatusPolling;
